<template>
  <div
    class="saved-view-selector"
  >
    <m-dropdown
      v-if="(selectedView.isTemp || showEditorForNoView) && !readOnly && showCreate"
      v-model:value="showEditorForNoView"
      block
      placement="bottomCenter"
      :relocate-key="showEditorForNoView"
      :title="$t('savedViewSelector.createView')"
    >
      <m-btn
        icon="plus"
        hide-border
        small
        light
        @click="showEditorForNoView = true"
      >
        {{ $t('savedViewSelector.addView') }}
      </m-btn>
      <template #overlay>
        <m-card
          no-padding
          class="_view-editor"
        >
          <saved-view-editor
            :params="selectedView.params"
            :goal-sort="selectedView.goalSort"
            :allowed-view-types="allowedViewTypes"
            :view-application="viewApplication"
            :no-private-views="noPrivateViews"
            :loading="loadingCreateView"
            @create="createView"
          />
        </m-card>
      </template>
    </m-dropdown>
    <m-dropdown
      v-else
      v-model:value="showViewList"
      :title="$t('savedViewSelector.selectView')"
      :disabled="!canShowViewList"
    >
      <m-btn
        hide-border
        small
        class="_btn"
        :disabled="!canShowViewList"
        @click="showViewList = true"
      >
        <div class="_inner">
          <m-icon
            v-if="selectedView.viewType !== undefined"
            :type="typeIcon"
          />
          <item-title
            :title="selectedView.title"
            :icons="[{ value: selectedView.emoji }]"
            class="_text"
          />
          <m-icon
            v-if="canShowViewList"
            type="down"
            size="10"
          />
        </div>
      </m-btn>
      <template #overlay>
        <m-card
          list
          no-padding
        >
          <saved-view-item
            v-for="view in sortedViews"
            :key="view.uid"
            :view="view"
            :active="view.uid === selectedView.uid"
            :allowed-view-types="allowedViewTypes"
            :view-application="viewApplication"
            :no-private-views="noPrivateViews"
            :has-unsaved-changes="viewsService.hasUnsavedChanges(view)"
            :read-only="readOnly"
            @select="selectView"
            @update="updateView"
            @delete="deleteView"
          />
          <template
            v-if="!readOnly && showCreate"
          >
            <m-divider xxs />
            <m-dropdown
              v-model:value="showEditorForViewList"
              block
              placement="topRight"
              :title="$t('savedViewSelector.createView')"
            >
              <m-card-item
                icon="plus"
                @click="showEditorForViewList = true"
              >
                {{ $t('savedViewSelector.addView') }}
              </m-card-item>
              <template #overlay>
                <m-card
                  no-padding
                  class="_view-editor"
                >
                  <saved-view-editor
                    :params="selectedView.params"
                    :allowed-view-types="allowedViewTypes"
                    :view-application="viewApplication"
                    :no-private-views="noPrivateViews"
                    :loading="loadingCreateView"
                    @create="createView"
                  />
                </m-card>
              </template>
            </m-dropdown>
          </template>
        </m-card>
      </template>
    </m-dropdown>
    <template
      v-if="!readOnly && viewsService.hasUnsavedChanges(selectedView)"
    >
      <m-btn
        small
        outlined
        color="warning"
        class="_save-btn"
        :loading="loadingUpdateView"
        @click="updateView(selectedView)"
      >
        <template v-if="$store.state.breakpoint.smAndDown">
          {{ $t('general.save') }}
        </template>
        <template v-else>
          <m-tooltip>
            <template v-if="selectedView.isPublic">
              {{ $t('savedViewSelector.saveForEveryone') }}
            </template>
            <template v-else>
              {{ $t('general.save') }}
            </template>
            <template #title>
              <template v-if="selectedView.isPublic">
                {{ $t('savedViewSelector.saveButtonTooltipPublic') }}
              </template>
              <template v-else>
                {{ $t('savedViewSelector.saveButtonTooltipPrivate') }}
              </template>
            </template>
          </m-tooltip>
        </template>
      </m-btn>
      <m-btn
        small
        hide-border
        light
        class="_reset-btn"
        @click="viewsService.resetView(selectedView.uid)"
      >
        {{ $t('savedViewSelector.reset') }}
      </m-btn>
    </template>
  </div>
</template>

<script>
import ItemTitle from '@/components/ItemTitle.vue';
import SavedViewEditor from '@/components/list/SavedViewEditor.vue';
import SavedViewItem from '@/components/list/SavedViewItem.vue';
import { VIEWS_SERVICE } from '@/lib/constants';
import { iconByType } from '@/lib/saved-view';
import { inject } from 'vue';
import { shallowCopy } from 'shared/lib/copy';

export default {
  name: 'SavedViewSelector',
  props: {
    showCreate: {
      type: Boolean,
      default: false,
    },
    allowedViewTypes: {
      type: Array,
      default: () => [],
    },
    viewApplication: {
      type: String,
      default: '',
    },
    propertyOption: {
      type: Object,
      default: () => null,
    },
    planning: {
      type: Object,
      default: () => null,
    },
    noPrivateViews: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  components: { SavedViewItem, SavedViewEditor, ItemTitle },
  setup() {
    const viewsService = inject(VIEWS_SERVICE);
    return {
      viewsService,
      loadingCreateView: viewsService.loadingCreateView,
      loadingUpdateView: viewsService.loadingUpdateView,
      views: viewsService.views,
      selectedView: viewsService.currentView,
    };
  },
  data() {
    return {
      showViewList: false,
      showEditorForNoView: false,
      showEditorForViewList: false,
    };
  },
  computed: {
    typeIcon() {
      return iconByType(this.selectedView.viewType);
    },
    sortedViews() {
      return shallowCopy(this.views).sort((a, b) => {
        if (a.isPublic && b.isPublic) {
          return 0;
        }
        if (a.isPublic) {
          return 1;
        }
        if (b.isPublic) {
          return -1;
        }
        return 0;
      });
    },
    canShowViewList() {
      return !this.readOnly || this.views.length > 1;
    },
  },
  methods: {
    createView({ view }) {
      this.viewsService.createView(view).then((view) => {
        this.$showSnackbar({ color: 'success', message: this.$t('success.saved') });
        this.viewsService.changeSelectedView(view);
        this.showEditorForViewList = false;
        this.showEditorForNoView = false;
      }).catch(() => {
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
      });
    },
    updateView(view) {
      this.viewsService.updateView(view)
        .then(() => {
          this.$showSnackbar({ color: 'success', message: this.$t('success.saved') });
        })
        .catch(() => {
          this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
        });
    },
    deleteView({ view }) {
      this.viewsService.deleteView(view).then(() => {
        this.$showSnackbar({ color: 'success', message: this.$t('success.deleted') });
      }).catch(() => {
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
      });
    },
    selectView({ view }) {
      this.viewsService.changeSelectedView(view);
      this.showViewList = false;
    },
  },
};
</script>

<style
    scoped
    lang="scss"
    type="text/scss"
>

  .saved-view-selector {
    display: flex;
    align-items: center;

    ._btn {
      ._inner {
        display: flex;
        align-items: center;

        ._text {
          max-width: 15rem;
          margin-right: .4rem;
          margin-left: .6rem;
          font-weight: $font-weight-semibold;
        }
      }
    }

    ._save-btn {
      margin-left: .8rem;
    }

    ._reset-btn {
      margin-left: .8rem;
    }
  }
</style>
